import React, { useEffect, useState, useMemo } from 'react';
import { debounce } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import styles from './AssetAnalysis.module.sass';
import { 
  setHistoricHolder, 
  setPairData, 
  setTopTraders, 
  setAssetHolder,
  isLoading 
} from '../../../redux/portfolioReducer';
import { 
  getHistoricHolderCount, 
  getDetailedHistoricHolders,
  getTokenPairData, 
  getTopTraders, 
  getAssetHolder 
} from 'services/portfolio';
import { useAuth } from 'react-oidc-context';
import { setSelectedContract, setSelectedToken } from '../../../redux/userReducer';

// Components
import HistoricalHolderCount from 'components/HistoricalHolderCount';
import SingleAssetMetrics from 'components/SingleAssetMetrics';
import AssetConcentrationSummary from 'components/AssetConcentrationSummary';
import PortfolioActionTable from 'components/PortfolioActionTable';
import HorizontalWallet from '../../../components/HorizontalWallet';

// added react memo here to prevent unnecessary re-rendering
const AssetAnalysis = React.memo(() => {
  const auth = useAuth();
  const dispatch = useDispatch();
  
  // Local state for HistoricalHolderCount - managed centrally here
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [modalData, setModalData] = useState(null);
  const [page, setPage] = useState(1);
  const [rowsPerPage] = useState(4);

  // Track data fetching state
  const [dataInitialized, setDataInitialized] = useState(false);
  
  // Track empty data states
  const [emptyDataStates, setEmptyDataStates] = useState({
    pairData: false,
    topTraders: false,
    assetHolder: false,
    detailedHolder: false
  });

  // Cache for API responses
  const [apiCache, setApiCache] = useState({
    historicHolder: null,
    pairData: null,
    topTraders: null,
    assetHolder: null,
    detailedHolder: null // New cache for detailed holder data
  });

  // Get required data from Redux
  const selectedContract = useSelector((state) => state.user.selectedContract);
  const selectedChain = useSelector((state) => state.user.selectedChain);
  const selectedToken = useSelector((state) => state.user.selectedToken);
  const loading = useSelector((state) => state.portfolio.loading);

  const debouncedSelectionChange = useMemo(
    () => debounce((index, contract, chain, token) => {
      handleSelectionChange(index, contract, chain, token);  // <-- Still calls your original function
    }, 300),
    []
  );


  // Initialize data on mount with cleanup on unmount
  useEffect(() => {
    async function initializeData() {
      if (dataInitialized || !auth.user?.access_token) return;
      dispatch(isLoading({ historicHolder: true }));
      
      try {
        // Get the initial list of assets and holder counts
        const historicData = await getHistoricHolderCount(auth.user.access_token);
        if (historicData) {
          dispatch(setHistoricHolder(historicData));
          setApiCache(prev => ({ ...prev, historicHolder: historicData }));
          
          // Initial selection will be handled in the next useEffect
          // after the data is properly loaded and transformed
        }
      } catch (error) {
        console.error('Error initializing data');
      } finally {
        dispatch(isLoading({ historicHolder: false }));
        setDataInitialized(true);
      }
    }
    
    // Only run this once when component mounts and auth is available
    initializeData();
    
    // Cleanup function to reset state when component unmounts
    return () => {
      dispatch(setSelectedContract(null));
      dispatch(setSelectedToken(null));
      dispatch(setHistoricHolder({}));
      dispatch(setPairData([]));
      dispatch(setTopTraders({}));
      dispatch(setAssetHolder({}));
    };
  }, [dispatch, dataInitialized, auth.user?.access_token]);

  // New effect to handle initializing the selection from the HistoricalHolderCount data
  useEffect(() => {
    const historicData = apiCache.historicHolder || {};
    
    // Skip if we're still loading or already have a selection
    if (loading?.historicHolder || selectedContract) {
      return;
    }
    
    // Skip if no data available
    if (!historicData || Object.keys(historicData).length === 0) {
      return;
    }
    
    // Transform data to get the first available token from the first page
    const transformData = (data) => {
      const result = {};
      const contract = {};
      
      for (const network in data) {
        if (data.hasOwnProperty(network)) {
          const assets = data[network];
          for (const asset in assets) {
            if (assets.hasOwnProperty(asset)) {
              const fullName = `${asset}-${network}`;
              result[fullName] = assets[asset]?.data || [];
              contract[fullName] = assets[asset]?.contract || null;
            }
          }
        }
      }
      
      return { result, contract };
    };
    
    const { result, contract } = transformData(historicData);
    
    // Sort by holder count (descending) to match the HistoricalHolderCount component's sorting
    const sortedKeys = Object.keys(result).sort(
      (a, b) => {
        const countA = result[a]?.[0]?.Holder_Count || 0;
        const countB = result[b]?.[0]?.Holder_Count || 0;
        return countB - countA;
      }
    );
    
    // Get first page of items based on rowsPerPage
    const paginatedKeys = sortedKeys.slice(0, rowsPerPage);
    
    // If we have valid data, set the first item as selected
    if (paginatedKeys.length > 0) {
      const firstItem = paginatedKeys[0];
      const [token, chain] = firstItem.split('-');
      
      if (token && chain && historicData[chain]?.[token]?.contract) {
        const contractAddress = historicData[chain][token].contract;
        
        // Set selection in Redux
        dispatch(setSelectedContract({
          contract: contractAddress,
          chain: chain
        }));
        dispatch(setSelectedToken(`${token}-${chain}`));
        
        // Set local selectedIndex to 0
        setSelectedIndex(0);
      }
    }
  }, [apiCache.historicHolder, loading?.historicHolder, selectedContract, dispatch, rowsPerPage]);

  // Handle dependent data fetching - only triggered when selection changes
  useEffect(() => {
    if (!selectedContract || !selectedChain || !auth.user?.access_token) return;

    if (loading?.historicHolder) return;

    const controller = new AbortController();
    // let isCanceled = false;
    let timeoutId = null;
    
    async function fetchDependentData() {
      // Set loading states
      dispatch(isLoading({
        pairData: true,
        topTraders: true,
        assetHolder: true,
        detailedHolder: true // New loading state
      }));
      
      // Add a timeout to cancel loading if taking too long
      // timeoutId = setTimeout(() => {
      //   if (!isCanceled) {
      //     console.warn('Data fetch timeout - clearing loading states');
      //     dispatch(isLoading({
      //       pairData: false,
      //       topTraders: false,
      //       assetHolder: false,
      //       detailedHolder: false
      //     }));
      //   }
      // }, 15000); // 15 second timeout

      timeoutId = setTimeout(() => {
        console.warn('Data fetch timeout - clearing loading states');
        dispatch(isLoading({
          pairData: false,
          topTraders: false,
          assetHolder: false,
          detailedHolder: false
        }));
      }, 15000);
      
      try {
        // Calculate fromDate (30 days ago)
        const today = new Date();
        const fromDate = new Date(today);
        fromDate.setDate(today.getDate() - 30);
        const fromDateString = fromDate.toISOString().split('T')[0]; // Format as YYYY-MM-DD
        
        const [pairResponse, tradersResponse, holderResponse, detailedHolder] = await Promise.all([
          getTokenPairData(auth.user.access_token, selectedContract, selectedChain, controller.signal),
          getTopTraders(auth.user.access_token, controller.signal),
          getAssetHolder(auth.user.access_token, controller.signal),
          getDetailedHistoricHolders(
            auth.user.access_token,
            selectedContract,
            fromDateString,
            selectedChain,
            "4h",
            controller.signal
          )
        ]);

        // Track which responses are empty
        const newEmptyStates = {
          pairData: Array.isArray(pairResponse) && pairResponse.length === 0,
          topTraders: !tradersResponse || Object.keys(tradersResponse).length === 0,
          assetHolder: !holderResponse || Object.keys(holderResponse).length === 0,
          detailedHolder: !detailedHolder || !detailedHolder.result || detailedHolder.result.length === 0
        };
        
        setEmptyDataStates(newEmptyStates);

        // Update detailed holder data - store in cache but don't affect the main display
        if (detailedHolder) {
          if (detailedHolder.result && detailedHolder.result.length > 0) {
            // Store the detailed holder data in a separate cache entry
            setApiCache(prev => ({ ...prev, detailedHolder: detailedHolder }));
            
            // Update the currently selected token in the Redux store with the detailed data
            if (selectedToken) {
              const [token, chain] = selectedToken.split('-');
              
              if (token && chain) {
                // Create a partial update only for the selected token
                const formattedDetailedData = {
                  [chain]: {
                    [token]: {
                      contract: selectedContract,
                      data: detailedHolder.result?.map(item => ({
                        Date: item.timestamp.split('T')[0],
                        Holder_Count: item.totalHolders,
                        timestamp: new Date(item.timestamp).getTime(),
                        netHolderChange: item.netHolderChange,
                        holderPercentChange: item.holderPercentChange,
                        newHoldersByAcquisition: item.newHoldersByAcquisition
                      })) || []
                    }
                  }
                };
                
                // Merge with existing data in Redux
                dispatch(setHistoricHolder(formattedDetailedData));
              }
            }
          } else {
            // Clear cache if response is empty
            setApiCache(prev => ({ ...prev, detailedHolder: null }));
          }
        }
        
        // Update Redux and cache only if component is still mounted
        if (Array.isArray(pairResponse)) {
          dispatch(setPairData(pairResponse));
          
          // Only cache if there's actual data
          if (pairResponse.length > 0) {
            setApiCache(prev => ({ ...prev, pairData: pairResponse }));
          } else {
            // Clear previous cached data when we get an empty response
            setApiCache(prev => ({ ...prev, pairData: null }));
          }
        }

        if (tradersResponse) {
          dispatch(setTopTraders(tradersResponse));
          
          if (Object.keys(tradersResponse).length > 0) {
            setApiCache(prev => ({ ...prev, topTraders: tradersResponse }));
          } else {
            setApiCache(prev => ({ ...prev, topTraders: null }));
          }
        }

        if (holderResponse) {
          dispatch(setAssetHolder(holderResponse));
          
          if (Object.keys(holderResponse).length > 0) {
            setApiCache(prev => ({ ...prev, assetHolder: holderResponse }));
          } else {
            setApiCache(prev => ({ ...prev, assetHolder: null }));
          }
        }
      } catch (error) {
        if (error.name === 'AbortError') return; // Ignore abort errors
        console.error('Error fetching dependent data');
        
        // Set everything to empty on error
        setEmptyDataStates({
          pairData: true,
          topTraders: true, 
          assetHolder: true,
          detailedHolder: true
        });
        
        // Clear cached data on error
        setApiCache(prev => ({
          ...prev,
          pairData: null,
          topTraders: null,
          assetHolder: null,
          detailedHolder: null
        }));
      } finally {
        // if (!isCanceled) {
          // Clear timeout and loading states
          if (timeoutId) clearTimeout(timeoutId);
          dispatch(isLoading({
            pairData: false,
            topTraders: false,
            assetHolder: false,
            detailedHolder: false
          }));
        // }
      }
    }

    fetchDependentData();

  // Cleanup function
  return () => {
      controller.abort();
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [selectedContract, selectedChain, auth.user?.access_token, dispatch, selectedToken]);

  // Handle selection change from HistoricalHolderCount - central handler
  const handleSelectionChange = (index, contract, chain, token) => {
    // Update the local selection index
    setSelectedIndex(index);
    
    if (contract && chain) {
      // Important: Clear cached dependent data to prevent stale data display
      setApiCache(prev => ({
        ...prev,
        pairData: null,
        topTraders: null,
        assetHolder: null,
        detailedHolder: null
      }));
      
      // Reset empty states
      setEmptyDataStates({
        pairData: false,
        topTraders: false,
        assetHolder: false,
        detailedHolder: false
      });
      
      // Pass object with both contract and chain properties
      dispatch(setSelectedContract({
        contract: contract,
        chain: chain
      }));
      dispatch(setSelectedToken(`${token}-${chain}`));
      
      // Reset any modal data when changing selection
      if (modalData) {
        setModalData(null);
      }
    }
  };

  return (
    <div className={styles.container}>
      {/* <HorizontalWallet /> */}
      <HorizontalWallet showWallets={false} />
      <div className={styles.leftColumn}>
        <div className={styles.metricsContainer}>
          <div className={styles.topMetrics}>
            <div className={`${styles.metricWrapper} ${styles.halfWidthMetric}`}>
              <HistoricalHolderCount 
                selectedIndex={selectedIndex}
                onSelectionChange={debouncedSelectionChange}
                modalData={modalData}
                setModalData={setModalData}
                page={page}
                setPage={setPage}
                rowsPerPage={rowsPerPage}
                cachedData={apiCache.historicHolder || {}}
                detailedData={apiCache.detailedHolder}
                selectedToken={selectedToken}
                isEmpty={emptyDataStates.detailedHolder}
              />
            </div>
          </div>
          <div className={`${styles.metricWrapper} ${styles.halfWidthMetric} ${styles.bottomMetric}`}>
            <SingleAssetMetrics 
              cachedData={apiCache.pairData}
              isEmpty={emptyDataStates.pairData}
              isLoading={loading?.pairData}
            />
          </div>
        </div>
      </div>
      <div className={styles.rightColumn}>
        <div className={`${styles.metricWrapper} ${styles.fullWidthMetric}`}>
          <AssetConcentrationSummary 
            cachedData={apiCache.pairData}
            isEmpty={emptyDataStates.pairData}
            isLoading={loading?.pairData}
          />
        </div>
        <div className={`${styles.metricWrapper} ${styles.fullWidthMetric}`}>
          <PortfolioActionTable 
            cachedData={{
              topTraders: apiCache.topTraders,
              assetHolder: apiCache.assetHolder,
              detailedHolder: apiCache.detailedHolder
            }}
            isEmpty={{
              topTraders: emptyDataStates.topTraders,
              assetHolder: emptyDataStates.assetHolder,
              detailedHolder: emptyDataStates.detailedHolder
            }}
            isLoading={{
              topTraders: loading?.topTraders,
              assetHolder: loading?.assetHolder,
              detailedHolder: loading?.detailedHolder
            }}
          />
        </div>
      </div>
    </div>
  );
});

export default AssetAnalysis;



